<template>
  <div>
    <h3 class="mb-3 input-text-size">Import Mortgage Payment File</h3>
    <div class="pb-2">
      <v-container class="py-0 px-0">
        <v-row dense>
          <v-col cols="12" md="4">
            <v-select
              v-model="selectedFileDefinition"
              :items="fileDefinitions"
              item-text="Name"
              item-value="_id"
              label="File Definition"
              outlined
              dense
              :loading="loadingDefinitions"
              :disabled="loadingDefinitions"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="postingDate"
              label="Posting Date"
              type="date"
              outlined
              dense
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="mortgageCompany"
              :items="mortgageCompanyOptions"
              item-text="text"
              item-value="value"
              label="Mortgage Company"
              outlined
              dense
              :loading="loadingMortgageCompanies"
              :disabled="loadingMortgageCompanies"
            />
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="6">
            <v-file-input
              v-model="localFile"
              label="Select Payment File from local system"
              outlined
              dense
              accept=".csv,.txt"
              prepend-icon=""
              prepend-inner-icon="mdi-paperclip"
              :disabled="!selectedFileDefinition"
              @change="handleLocalFileSelected"
            />
          </v-col>
          <v-col cols="6">
            <v-select
              v-model="selectedFtpFile"
              :items="ftpFiles"
              label="Select Payment from FTP"
              outlined
              dense
              :loading="loadingFtpFiles"
              :disabled="!selectedFileDefinition || loadingFtpFiles"
              :no-data-text="ftpNoDataText"
              prepend-inner-icon="mdi-server-network"
              clearable
              @change="handleFtpFileSelected"
            >
              <template v-slot:append-outer>
                <v-btn
                  icon
                  small
                  :disabled="!selectedFileDefinition"
                  :loading="loadingFtpFiles"
                  @click="loadFtpFiles"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
            </v-select>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12">
            <v-alert
              v-if="errorMessage"
              type="error"
              dense
              dismissible
              @input="errorMessage = null"
            >
              {{ errorMessage }}
            </v-alert>
            <v-alert
              v-if="successMessage"
              type="success"
              dense
              dismissible
              @input="successMessage = null"
            >
              {{ successMessage }}
            </v-alert>
          </v-col>
        </v-row>
      </v-container>
    </div>
    <div class="mt-3">
      <v-btn
        color="primary"
        small
        rounded
        class="input-text-size"
        :disabled="!selectedFileDefinition"
        @click="downloadSpec"
      >
        Download Spec
      </v-btn>
      <v-btn
        color="primary"
        small
        rounded
        class="input-text-size ml-2"
        :disabled="!canUpload"
        :loading="uploading"
        @click="uploadFile"
      >
        Upload File
      </v-btn>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { clients } from '../../../util/clients';
import {
  ftpEmptyMessage,
  ftpErrorMessage,
  readFtpFileList,
  readFtpPath,
} from '../../../util/shared/ftp-file-list';

export default {
  name: 'MortgagePaymentUploadForm',
  data() {
    return {
      selectedFileDefinition: null,
      postingDate: null,
      mortgageCompany: '',
      selectedFile: null,
      localFile: null,
      selectedFtpFile: null,
      ftpFiles: [],
      fileDefinitions: [],
      mortgageCompanyOptions: [],
      loadingDefinitions: false,
      loadingMortgageCompanies: false,
      loadingFtpFiles: false,
      // Why the FTP list is empty, shown inside the dropdown (see ftp-file-list.js).
      ftpListMessage: null,
      uploading: false,
      errorMessage: null,
      successMessage: null,
      baseCrudKey: 'mtgPayFileDefinitionsBaseCrudKey',
    };
  },
  computed: {
    ...mapGetters('base/crud', ['items']),
    canUpload() {
      return this.selectedFileDefinition
        && this.postingDate
        && this.mortgageCompany
        && this.selectedFile
        && !this.uploading;
    },
    selectedDefinition() {
      return this.fileDefinitions.find((def) => def._id === this.selectedFileDefinition);
    },
    ftpNoDataText() {
      if (this.loadingFtpFiles) return 'Reading the FTP server...';
      if (this.ftpListMessage) return this.ftpListMessage;
      return 'Select a file definition to list the FTP files';
    },
  },
  async mounted() {
    await Promise.all([
      this.loadFileDefinitions(),
      this.loadMortgageCompanies(),
    ]);
    // Set default posting date to today
    this.postingDate = this.$store.getters.todaysDate;
  },
  methods: {
    ...mapActions('config', {
      loadFileDefinitionsAction: 'loadFileDefinitions',
    }),
    ...mapActions('fileQueue', [
      'upsertFileQueue',
    ]),
    ...mapActions('lists', {
      loadListByName: 'loadListByName',
    }),
    async loadFileDefinitions() {
      try {
        this.loadingDefinitions = true;
        await this.loadFileDefinitionsAction({
          criteria: {
            Program: 'MTGPAY',
          },
          fields: {
            _id: 1,
            Name: 1,
            Program: 1,
            FileType: 1,
            ColumnDefinition: {
              ColumnNumber: 1,
              Name: 1,
              MapToTable: 1,
              MapToColumn: 1,
              StartPosition: 1,
              Length: 1,
              FieldType: 1,
            },
          },
          baseCrudKey: this.baseCrudKey,
        });
        // Get from store after it's loaded
        this.fileDefinitions = this.items(this.baseCrudKey) || [];
      } catch (error) {
        console.error('Error loading file definitions:', error);
        this.errorMessage = 'Failed to load file definitions';
      } finally {
        this.loadingDefinitions = false;
      }
    },
    async loadMortgageCompanies() {
      try {
        this.loadingMortgageCompanies = true;
        const list = await this.loadListByName({
          name: 'Mortgage Companies',
          fields: {
            _id: 1,
            Name: 1,
            DataDefinition: {
              ShortName: 1,
              LongName: 1,
              Removed: 1,
            },
          },
        });
        if (list && list.DataDefinition) {
          this.mortgageCompanyOptions = list.DataDefinition
            .filter((item) => !item.Removed)
            .map((item) => ({
              text: item.LongName || item.ShortName,
              value: item.LongName || item.ShortName,
            }));
        }
      } catch (error) {
        console.error('Error loading mortgage companies:', error);
        this.errorMessage = 'Failed to load mortgage companies';
      } finally {
        this.loadingMortgageCompanies = false;
      }
    },
    async loadFtpFiles() {
      if (!this.selectedFileDefinition) {
        this.ftpFiles = [];
        this.ftpListMessage = null;
        return;
      }
      this.loadingFtpFiles = true;
      this.errorMessage = null;
      this.ftpListMessage = null;
      try {
        const { backendRest } = clients.direct;
        const response = await backendRest.get('/mortgage/payment-import/ftp/files', {
          params: {
            fileDefinitionId: this.selectedFileDefinition,
            mortgageCompany: this.mortgageCompany,
          },
        });
        const files = readFtpFileList(response.data);
        this.ftpFiles = files.map((file) => {
          if (typeof file === 'string') return file;
          return (file && (file.fileName || file.name)) || '';
        }).filter((file) => !!file);
        if (!this.ftpFiles.length) {
          this.ftpListMessage = ftpEmptyMessage(readFtpPath(response.data));
        }
      } catch (error) {
        console.error('Error loading FTP files:', error);
        // The reason the FTP server gave, rather than "Failed to load FTP files" -
        // see TKT-000856.
        this.ftpListMessage = ftpErrorMessage(error);
        this.errorMessage = this.ftpListMessage;
        this.ftpFiles = [];
      } finally {
        this.loadingFtpFiles = false;
      }
    },
    handleLocalFileSelected() {
      this.selectedFile = this.localFile;
      this.selectedFtpFile = null;
    },
    async handleFtpFileSelected() {
      if (!this.mortgageCompany) {
        this.mortgageCompany = null;
        return;
      }
      this.loadingFtpFiles = true;
      this.errorMessage = null;
      try {
        const { backendRest } = clients.direct;
        const response = await backendRest.get('/mortgage/payment-import/ftp/file', {
          params: {
            fileName: this.selectedFtpFile,
            fileDefinitionId: this.selectedFileDefinition,
            mortgageCompany: this.mortgageCompany,
          },
          responseType: 'blob',
        });
        const contentType = response.headers['content-type'] || 'text/plain';
        const ftpFile = new File(
          [response.data],
          this.selectedFtpFile,
          { type: contentType },
        );
        this.selectedFile = ftpFile;
        this.localFile = null;
        this.successMessage = `FTP file "${ftpFile.name}" loaded successfully`;
      } catch (error) {
        console.error('Error downloading FTP file:', error);
        this.errorMessage = 'Failed to download FTP file';
        this.selectedFile = null;
      } finally {
        this.loadingFtpFiles = false;
      }
    },
    async downloadSpec() {
      if (!this.selectedDefinition) {
        return;
      }

      try {
        const definition = this.selectedDefinition;
        const filename = `${definition.Name.replace(/[^a-z0-9]/gi, '_')}_spec.txt`;

        // Use axios with blob response type
        const { backendRest } = clients.direct;
        const response = await backendRest.get(
          `/fileDefinition/download/${definition._id}`,
          {
            responseType: 'blob',
          },
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error downloading spec:', error);
        this.errorMessage = 'Failed to download specification';
      }
    },
    async uploadFile() {
      try {
        this.uploading = true;
        this.errorMessage = null;
        this.successMessage = null;

        const definition = this.selectedDefinition;
        if (!definition) {
          this.errorMessage = 'Please select a file definition';
          return;
        }

        const timestamp = new Date();
        const extension = definition.FileType === 'CSV' ? 'csv' : 'txt';
        const fileName = `PaymentImport_${this.mortgageCompany}_${timestamp.getTime()}.${extension}`;

        // Upload file to backend first
        const formData = new FormData();
        formData.append('data', this.selectedFile);
        formData.append('fileName', fileName);

        const { backendRest } = clients.direct;
        const uploadResponse = await backendRest.post('/payments/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Insert into file queue with Pending status
        const queueItem = {
          timestamp,
          FileName: fileName,
          FilePath: uploadResponse.data.filePath || '',
          Program: 'MTGPAY',
          FileDefinitionId: this.selectedFileDefinition,
          FileDefinitionName: definition.Name,
          Status: 'Pending',
          Records: 0,
          TotalRecords: 0,
          Errors: 0,
          Parameters: {
            postingDate: this.postingDate,
            mortgageCompany: this.mortgageCompany,
          },
        };

        await this.upsertFileQueue({
          item: queueItem,
          fields: {
            _id: 1,
          },
        });

        this.successMessage = 'File uploaded and queued successfully';
        this.$emit('upload-queued');

        // Reset form
        this.selectedFile = null;
      } catch (error) {
        console.error('Error uploading file:', error);
        this.errorMessage = 'Failed to upload file';
      } finally {
        this.uploading = false;
      }
    },
  },
  watch: {
    mortgageCompany(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.selectedFile = null;
        this.localFile = null;
        this.selectedFtpFile = null;
        this.loadFtpFiles();
      }
    },
  },
};
</script>

<style scoped>
</style>
